// SPORE SAYS state machine (pure): TITLE → OPENING → RITUAL (touch test) → RULES (teaches the sounds) →
// [(INTRO) → INSTRUCT → DEMO (audio only) → TRY (→ WRONG → DEMO …) → UNLOCK → INTERPRET → ANALYSIS → MEMORY (→ VOLUNTEER)] ×12 → FINALE → SEARCH → BROOCH → MMMM → CLOSING → END (CALM: tendril game).
import { SAYS_LEVELS, SAYS_CONFIG, RULES_TEACH, MEMORY_FRAGMENTS, isOff, offMsOf } from './saysConfig';
import { classifySymbol } from '../spore/binaryTouch';
import { adaptSequences } from './wordGuard';

export function createSaysMachine({ levels = SAYS_LEVELS, config = SAYS_CONFIG, isSpeaking = () => false, speechTail = () => Infinity, onCue = () => {}, isDegraded = () => false, vetSolve = () => null } = {}) {
  const C = config;
  let s;
  let lastT = null;
  let seqs = levels.map((l) => [...l.sequence]); // phrases, rewritten when a word is disabled
  let disabled = new Set();
  const fallback = () => disabled.has('TAP') && disabled.has('STROKE'); // touch hardware unreliable: any input counts
  const allOff = () => disabled.size >= 3; // nothing can be read: the host decodes with N
  function fresh() {
    seqs = levels.map((l) => [...l.sequence]);
    disabled = new Set();
    s = { phase: 'title', paused: false, phaseMs: 0, levelIdx: 0, stepIndex: 0, attempt: 1, failures: 0, lenient: 0, nudged: false, hostHint: false,
      levelMs: 0, tried: false, sinceInputMs: 0, offMs: 0, touchInTry: false, demoIdx: -1, sung: -1, lastSymbol: null, lastResult: null, discovered: [], guesses: {}, touching: false,
      hovering: false, teach: [], teachIdx: -1, teachNext: null, wrongLineUsed: false, wrongLinePending: false, wrongSaid: false, wrongN: 0, encourageUsed: false, encouragePending: false, encN: 0, volN: 0, offFails: 0, offForgive: false };
  }
  fresh();
  const lv = () => levels[s.levelIdx];
  const seq = () => seqs[s.levelIdx];
  const say = (id, kind, priority) => onCue({ type: 'speak', id, kind, priority });
  const setPhase = (p) => {
    s.phase = p;
    s.phaseMs = 0;
    onCue({ type: 'phase', phase: p, feeling: lv() && lv().feeling, level: s.levelIdx + 1 });
  };
  const talkDone = (min, max = C.maxTalkMs) => s.phaseMs >= min && (!isSpeaking() || s.phaseMs >= max);
  const demoPlan = () => {
    let at = C.demo.preMs;
    return seq().map((sym) => {
      const item = { sym, at, ms: C.demo[sym] };
      at += C.demo[sym] + C.demo.gapMs;
      return item;
    });
  };
  const demoTotal = () => {
    const p = demoPlan();
    return p[p.length - 1].at + p[p.length - 1].ms + C.demo.endMs;
  };

  // teaching beats: Henri's line, then SPORE's sound for it, one after the other
  function teach(items, phase, next) {
    Object.assign(s, { teach: items, teachIdx: -1, teachNext: next });
    setPhase(phase);
    stepTeach();
  }
  function stepTeach() {
    s.teachIdx += 1;
    s.phaseMs = 0;
    const it = s.teach[s.teachIdx];
    if (!it) return s.teachNext === 'level0' ? startLevel(0) : instruct();
    if (it.say) say(it.say, 'opening', 3);
    else onCue({ type: 'sing', symbol: it.sing, index: 0, ms: C.demo[it.sing], teach: true });
    return null;
  }
  function teachTick() {
    const it = s.teach[s.teachIdx];
    if (it.say ? talkDone(C.teachGapMs) : s.phaseMs >= C.demo[it.sing] + C.teachGapMs) stepTeach();
  }
  function instruct() {
    setPhase('instruct');
    say(`${lv().feeling}.instruct`, 'hint', 3);
  }
  function startLevel(i) {
    s.levelIdx = i;
    Object.assign(s, { stepIndex: 0, attempt: 1, failures: 0, lenient: 0, encourageUsed: false, encouragePending: false, nudged: false, hostHint: false, levelMs: 0, tried: false, sinceInputMs: 0, offMs: 0, touchInTry: false, demoIdx: -1, sung: -1, lastSymbol: null, lastResult: null, wrongLineUsed: false, wrongLinePending: false, wrongSaid: false, offFails: 0, offForgive: false });
    if (lv().intro) teach(lv().intro, 'intro', 'instruct');
    else instruct();
  }
  function sing() {
    Object.assign(s, { stepIndex: 0, offMs: 0, demoIdx: -1, sung: -1, touchInTry: false });
    setPhase('demo');
  }
  function startTry() {
    Object.assign(s, { stepIndex: 0, offMs: 0, sinceInputMs: 0, touchInTry: false, demoIdx: -1, tried: true, solve: [] });
    setPhase('try');
  }
  function unlock() {
    s.discovered.push(lv().feeling);
    s.stepIndex = seq().length;
    setPhase('unlock');
    onCue({ type: 'unlock', feeling: lv().feeling, level: s.levelIdx + 1 });
  }
  function wrong(got) {
    s.failures += 1;
    s.attempt += 1;
    s.lenient = s.failures >= C.lenientAfter[1] ? 2 : s.failures >= C.lenientAfter[0] ? 1 : 0;
    s.lastResult = { ok: false, got, expected: seq()[s.stepIndex] };
    s.wrongLinePending = !s.wrongLineUsed; // Henri's cheeky line at most once per experiment
    s.wrongLineUsed = true;
    // leniency just kicked in: Henri's encouragement instead (at most once per experiment), never the wrong line too
    if (s.lenient >= 1 && !s.encourageUsed) Object.assign(s, { encourageUsed: true, encouragePending: true, wrongLinePending: false });
    s.wrongSaid = false;
    setPhase('wrong');
    onCue({ type: 'wrong', got, attempt: s.attempt, lenient: s.lenient });
  }
  // solve reads are timed at press ONSET: a long massage then quick taps is human rhythm, not a metronome
  function advance(word, t = lastT) {
    if (word && !fallback()) (s.solve || (s.solve = [])).push({ word, t });
    s.stepIndex += 1;
    s.offMs = 0;
    s.sinceInputMs = 0;
    if (s.stepIndex >= seq().length) {
      // suspicious solve (phantom hardware): the host side disables the word, the phrase is re-sung adapted
      const veto = !fallback() && !allOff() && s.solve && s.solve.length ? vetSolve(s.solve.slice(), lv().feeling) : null;
      if (veto) {
        s.lastResult = { ok: false, vetoed: veto };
        if (s.phase === 'try') sing();
        return;
      }
      unlock();
    }
    else onCue({ type: 'step', stepIndex: s.stepIndex });
  }

  function tryTick(dt, ts) {
    s.sinceInputMs += dt;
    if (s.levelMs >= C.hostHintAfterS * 1000) s.hostHint = true;
    if (!s.nudged && s.levelMs >= C.nudgeAfterS * 1000 && !isSpeaking()) {
      s.nudged = true;
      say(`${lv().feeling}.nudge`, 'hint', 2);
    }
    const exp = seq()[s.stepIndex];
    if (isOff(exp)) {
      if (!ts.touching || s.offForgive || fallback() || allOff()) s.offMs += dt; // phantom input can't block a hands-off step forever
      if (s.offMs >= offMsOf(exp)) advance();
    } else if (s.stepIndex > 0 && !ts.touching && !s.touchInTry && s.sinceInputMs >= C.timeoutMs) {
      s.stepIndex = 0;
      s.sinceInputMs = 0;
      s.lastResult = { ok: false, timeout: true };
      onCue({ type: 'timeout' });
    }
  }

  function tick(t, ts = {}) {
    const dt = lastT === null ? 0 : Math.max(0, Math.min(250, t - lastT));
    lastT = t;
    s.touching = !!ts.touching;
    s.hovering = !!ts.hovering;
    if (s.phase === 'title' || s.phase === 'end' || s.paused || !dt) return;
    s.phaseMs += dt;
    const P = s.phase;
    if (s.tried && ['demo', 'try', 'wrong'].includes(P)) s.levelMs += dt;
    if (P === 'opening' && talkDone(C.openingMinMs, C.storyMaxMs)) setPhase('ritual'); // greeting + prologue run long
    else if (P === 'ritual' && s.phaseMs >= C.ritualMaxMs) api.ritualDone();
    else if (P === 'rules' || P === 'intro') teachTick();
    else if (P === 'volunteer' && talkDone(C.volunteerMinMs)) startLevel(s.levelIdx + 1);
    else if (P === 'instruct' && talkDone(C.instructMinMs)) sing();
    else if (P === 'demo') {
      const plan = demoPlan();
      s.demoIdx = plan.findIndex((x) => s.phaseMs >= x.at && s.phaseMs < x.at + x.ms);
      if (s.demoIdx > s.sung) {
        s.sung = s.demoIdx;
        onCue({ type: 'sing', symbol: plan[s.demoIdx].sym, index: s.demoIdx, ms: plan[s.demoIdx].ms });
      }
      if (s.phaseMs >= demoTotal()) startTry();
    } else if (P === 'try') tryTick(dt, ts);
    else if (P === 'wrong') {
      if (s.encouragePending && s.phaseMs >= C.wrongLineAtMs) {
        s.encouragePending = false;
        s.wrongSaid = true;
        say(`encourage.${(s.encN % C.encourageLines) + 1}`, 'hint', 2);
        s.encN += 1;
      }
      if (s.wrongLinePending && s.phaseMs >= C.wrongLineAtMs) {
        s.wrongLinePending = false;
        s.wrongSaid = true;
        say(`wrong.${(s.wrongN % C.wrongLines) + 1}`, 'hint', 2);
        s.wrongN += 1;
      }
      if (s.phaseMs >= C.wrongMs && !s.wrongLinePending && !s.encouragePending && (!s.wrongSaid || talkDone(C.wrongMs))) sing();
    }
    else if (P === 'unlock' && s.phaseMs >= C.unlockMs) {
      setPhase('interpret');
      say(`${lv().feeling}.interpret`, 'interpret', 4);
      onCue({ type: 'guess', feeling: lv().feeling });
    } else if (P === 'interpret' && talkDone(C.interpretMinMs, C.storyMaxMs)) setPhase('analysis'); // the stamps wait for his line to end
    else if (P === 'analysis' && s.phaseMs >= C.analysisMs && !isSpeaking()) {
      setPhase('memory'); // story: SPORE gives back one memory fragment of Margaux, Henri reacts
      s.mem = { step: 'beat', stepMs: 0, cardMs: 0, voiceMs: 0, words: (MEMORY_FRAGMENTS[lv().feeling] || '').split(/\s+/).filter(Boolean).length };
      onCue({ type: 'memory-beat', feeling: lv().feeling });
    } else if (P === 'memory' && memoryTick(dt)) {
      const next = s.levelIdx + 1;
      if (next < levels.length && next % C.volunteerEvery === 0) {
        setPhase('volunteer'); // fresh fingers after experiments 2, 4, 6, 8, 10
        say(`volunteer.${(s.volN % C.volunteerLines) + 1}`, 'hint', 3);
        s.volN += 1;
      } else if (next < levels.length) startLevel(next);
      else api.finale();
    } else if (P === 'finale' && talkDone(4000, C.storyMaxMs)) setPhase('search'); // waits (indefinitely) for the brooch
    else if (P === 'brooch' && talkDone(C.broochMinMs, C.storyMaxMs)) {
      setPhase('mmmm');
      onCue({ type: 'mmmm', ms: C.mmmmMs });
    } else if (P === 'mmmm' && s.phaseMs >= C.mmmmMs) {
      setPhase('closing');
      say('closing', 'finale', 3);
    } else if (P === 'closing' && talkDone(C.closingMinMs)) {
      setPhase('end');
      onCue({ type: 'end' });
    }
  }

  // BEAT → CUE → CARD (fade in + silent read) → VOICE → HOLD → FADEOUT → AFTER; true when the sequence is done
  function memoryTick(dt) {
    const SP = C.storyPacing;
    const m = s.mem;
    m.stepMs += dt;
    if (['card', 'voice', 'hold'].includes(m.step)) m.cardMs += dt;
    const go = (step) => Object.assign(m, { step, stepMs: 0 });
    if (m.step === 'beat' && m.stepMs >= SP.beatMs) {
      go('cue');
      onCue({ type: 'memory', feeling: lv().feeling, level: s.levelIdx + 1 });
    } else if (m.step === 'cue' && m.stepMs >= SP.cueMs) go('card');
    else if (m.step === 'card' && m.stepMs >= SP.fadeInMs + SP.readHoldMs) {
      go('voice');
      say(`${lv().feeling}.memory`, 'interpret', 4);
    } else if (m.step === 'voice' && m.stepMs >= 300 && (!isSpeaking() || m.stepMs >= C.storyMaxMs)) {
      m.voiceMs = m.stepMs;
      go('hold');
    } else if (m.step === 'hold' && (m.skip || (m.stepMs >= SP.postVoiceHoldMs && m.cardMs >= memoryMinCardMs()))) go('fadeout');
    else if (m.step === 'fadeout' && m.stepMs >= SP.fadeOutMs) go('after');
    else if (m.step === 'after' && m.stepMs >= SP.afterMs) return true;
    return false;
  }
  const memoryMinCardMs = () => Math.max(C.storyPacing.minCardMs, s.mem.words * C.storyPacing.msPerWord + s.mem.voiceMs + C.storyPacing.postVoiceHoldMs);
  // host N during a memory card: on to the next step
  function skipMemoryStep() {
    const m = s.mem;
    const next = { beat: 'cue', cue: 'card', card: 'voice', voice: 'hold', hold: 'fadeout', fadeout: 'after' }[m.step];
    if (!next) return;
    if (next === 'voice') say(`${lv().feeling}.memory`, 'interpret', 4);
    if (next === 'cue') onCue({ type: 'memory', feeling: lv().feeling, level: s.levelIdx + 1 });
    Object.assign(m, { step: next, stepMs: 0, skip: next === 'hold' || m.skip });
    if (next === 'hold') m.voiceMs = 0;
  }

  const api = {
    tick,
    touchStart() {
      if (s.phase !== 'try' || allOff()) return;
      s.touchInTry = true;
      s.sinceInputMs = 0;
      if (isOff(seq()[s.stepIndex])) {
        if (s.offForgive || fallback()) s.touchInTry = false;
        else if (s.lenient >= 2) s.offMs = 0;
        else {
          s.touchInTry = false;
          s.offFails += 1;
          if (disabled.size && s.offFails >= C.offForgiveAfter) s.offForgive = true; // 3 hands-off steps broken while a word is faulty
          wrong('TOUCH');
        }
      }
    },
    symbol(durationMs) {
      if (s.phase !== 'try' || !s.touchInTry) return null;
      s.touchInTry = false;
      s.sinceInputMs = 0;
      const exp = seq()[s.stepIndex];
      if (isOff(exp) || allOff()) return null;
      if (fallback()) {
        s.lastResult = { ok: true, got: exp, fallback: true };
        advance();
        return exp;
      }
      const got = classifySymbol(durationMs, { expected: exp, lenient: s.lenient }).symbol; // ● < 0.8 s · ▬ 1.0–4.0 s · ≋ ≥ 5.0 s
      if (disabled.has(got)) return null; // a disabled word is neither input nor a wrong attempt
      s.lastSymbol = got;
      if (got === exp) {
        s.lastResult = { ok: true, got };
        advance(got, lastT - durationMs);
      } else wrong(got);
      return got;
    },
    start() {
      if (s.phase !== 'title') return;
      setPhase('opening');
      ['opening', 'prologue.1', 'prologue.2', 'prologue.3'].forEach((id) => say(id, 'opening', 3)); // greeting, then Margaux's story
    },
    // ground-floor finale: Henri asks the assistants to fetch the robin brooch from the 4th floor
    finale() {
      setPhase('finale');
      say(s.discovered.length >= levels.length ? 'finale.request' : 'finale.partial', 'finale', 3);
    },
    broochFound() {
      if (!['finale', 'search'].includes(s.phase)) return false;
      setPhase('brooch');
      say('finale.reveal', 'finale', 4);
      say('finale.farewell', 'finale', 4);
      return true;
    },
    // calm-down game: the experiment is suspended at a safe boundary and re-sung on return
    enterCalm() {
      if (!['intro', 'instruct', 'demo', 'try', 'wrong'].includes(s.phase)) return false;
      s.calmFrom = s.phase;
      setPhase('calm');
      return true;
    },
    exitCalm() {
      if (s.phase !== 'calm') return false;
      Object.assign(s, { wrongLinePending: false, sinceInputMs: 0 });
      sing();
      return true;
    },
    ritualDone() {
      if (s.phase !== 'ritual') return;
      teach(RULES_TEACH, 'rules', 'level0');
    },
    togglePause() {
      if (s.phase === 'title') return api.start();
      s.paused = !s.paused;
      onCue({ type: 'pause', paused: s.paused });
      return null;
    },
    unlockNow() {
      if (s.phase === 'memory') skipMemoryStep();
      else if (['intro', 'instruct', 'demo', 'try', 'wrong'].includes(s.phase)) unlock();
    },
    resing() {
      if (['try', 'wrong'].includes(s.phase)) sing();
    },
    jump(i) {
      if (['title', 'end', 'opening', 'ritual', 'calm'].includes(s.phase) || !levels[i]) return;
      s.discovered = s.discovered.filter((f) => levels.findIndex((l) => l.feeling === f) < i);
      startLevel(i);
    },
    nudgeNow() {
      if (s.phase !== 'try') return;
      s.nudged = true;
      say(`${lv().feeling}.nudge`, 'hint', 2);
    },
    // silent re-write of the remaining phrases; a running demo / try re-sings the adapted phrase
    setWords(set) {
      disabled = new Set(set);
      const before = seq() ? seq().join() : '';
      seqs = adaptSequences(levels, disabled, s.levelIdx);
      if (['demo', 'try', 'wrong'].includes(s.phase) && seq().join() !== before) sing();
    },
    setGuess(f, g) {
      s.guesses[f] = g;
    },
    reset() {
      fresh();
      onCue({ type: 'reset' });
    },
    getSnapshot: () => ({
      ...s, discovered: [...s.discovered], guesses: { ...s.guesses }, feelings: levels.map((l) => l.feeling),
      level: s.levelIdx + 1, levelFeeling: lv() ? lv().feeling : null, sequence: lv() ? [...seq()] : [], tryS: s.levelMs / 1000,
      offTargetMs: lv() && isOff(seq()[s.stepIndex]) ? offMsOf(seq()[s.stepIndex]) : 5000, timeoutMs: C.timeoutMs,
      memStep: s.phase === 'memory' && s.mem ? s.mem.step : null, memCardMs: s.phase === 'memory' && s.mem ? s.mem.cardMs : 0, memMinCardMs: s.phase === 'memory' && s.mem && s.mem.step === 'hold' ? memoryMinCardMs() : null,
      adaptedSequences: seqs.map((x) => [...x]), disabledWords: [...disabled], fallbackMode: allOff() ? 'host-n' : fallback() ? 'any-input' : null,
      storyAct: ['title', 'opening', 'ritual', 'rules'].includes(s.phase) ? 0 : ['finale', 'search', 'brooch', 'mmmm', 'closing', 'end'].includes(s.phase) ? 'finale' : Math.min(3, Math.floor(s.levelIdx / 4) + 1),
    }),
  };
  return api;
}
